import React, { useEffect, useState } from 'react';
import { Clock, Users, Image, Key, Upload, Plus, Play, GroupIcon, User, AlertCircle } from 'lucide-react';
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DashboardApi } from '../../../apis/DashboardApi';
import SubscriptionBanner from './SubscriptionBanner';
import SubscriptionModal from './SubscriptionModal';

interface User {
  name: string;
  avatar: string;
}

interface StorageProps {
  used: number;
  total: number;
}

const ActionCard = ({ icon: Icon, title, link }: { icon: React.ElementType; title: string; link: string }) => (
  <NavLink to={link}>
    <div className="bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow cursor-pointer flex flex-col items-center justify-center space-y-4">
      <Icon className="w-6 h-6 text-orange-500" />
      <span className="text-sm font-medium text-gray-900">{title}</span>
    </div>
  </NavLink>
);

const StorageBar: React.FC<StorageProps> = ({ used, total }) => {
  const percentage = (used / total) * 100;
  return (
    <div className="w-full mt-2">
      <div className="flex justify-between text-sm mb-1">
        <span>{used}GB</span>
        <span>{total}GB</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full">
        <div
          className="h-full bg-blue-600 rounded-full"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const Dashboard = () => {
  const userLoginData = useSelector((state: any) => state.data.login.value);
  const [subscription, setSubscription] = useState<any>(null);
  const [bannerViewed, setBannerViewed] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [modalStatusLoading, setModalStatusLoading] = useState(true);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        const response: any = await DashboardApi.getUserSubscription();
        if (response?.data?.status === 'success') {
          setSubscription(response.data.data.subscription);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
      }
    };

    fetchSubscriptionData();
  }, []);

  // Check subscription modal status
  useEffect(() => {
    const checkModalStatus = async () => {
      try {
        const response: any = await DashboardApi.getSubscriptionModalStatus();
        if (response?.data?.status === 'success') {
          const hasViewedModal = response.data.data?.subscription_modal_viewed || false;
          // Show modal only if user hasn't viewed it yet
          setShowSubscriptionModal(!hasViewedModal);
        }
      } catch (error) {
        console.error('Error checking modal status:', error);
        // Don't show modal if there's an error
        setShowSubscriptionModal(false);
      } finally {
        setModalStatusLoading(false);
      }
    };

    if (userLoginData?.data?.user_data) {
      checkModalStatus();
    }
  }, [userLoginData]);

  // Handle modal close
  const handleCloseModal = async () => {
    try {
      await DashboardApi.markSubscriptionModalViewed();
      setShowSubscriptionModal(false);
    } catch (error) {
      console.error('Error marking modal as viewed:', error);
      // Close modal anyway to prevent it from being stuck
      setShowSubscriptionModal(false);
    }
  };

  // Handle plan selection
  const handlePlanSelect = async (planId: number) => {
    try {
      await DashboardApi.markSubscriptionModalViewed();
      // The modal component will handle navigation for paid plans
      // For free plan, it will just close the modal
    } catch (error) {
      console.error('Error marking modal as viewed:', error);
    }
  };

  // Check if the subscription needs renewal soon
  const needsRenewalSoon = subscription && (
    subscription.status === 'cancelled' ||
    subscription.expiration_notified_at ||
    (subscription.status === 'active' && !subscription.auto_renew &&
      new Date(subscription.end_date) < new Date(new Date().setDate(new Date().getDate() + 14)))
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Subscription Alert Banner */}
      <SubscriptionBanner onView={() => setBannerViewed(true)} />

      {/* Subscription Modal */}
      {!modalStatusLoading && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={handleCloseModal}
          onPlanSelect={handlePlanSelect}
        />
      )}

      <div className="flex flex-col md:flex-row">
        <Sidebar />

        {/* Main Content */}
        <main className="flex-1 p-6 relative md:ml-64">
          <Navbar />

          <h1 className="text-2xl font-bold mb-2">Welcome, {(userLoginData?.data?.user_data?.first_name)?.split(' ')[0]}</h1>

          {/* Getting Started Description */}
          <p className="text-gray-900 mb-8">
            Get started by creating a recipient, then add media to share with them.
          </p>

          {/* Subscription Info Card - Show if needs renewal and banner was dismissed */}
          {needsRenewalSoon && bannerViewed && (
            <div className="mb-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start">
                <AlertCircle className="text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-amber-800 mb-1">Subscription Attention Required</h3>
                  <p className="text-sm text-amber-700">
                    {subscription.status === 'cancelled'
                      ? `Your subscription has been cancelled and will expire on ${new Date(subscription.end_date).toLocaleDateString()}.`
                      : subscription.expiration_notified_at
                        ? `Your subscription has expired. Please renew to keep access to all features.`
                        : `Your subscription will expire on ${new Date(subscription.end_date).toLocaleDateString()} and auto-renewal is disabled.`
                    }
                  </p>
                  <NavLink
                    to={'/upgrade'}
                    className="mt-2 inline-block text-sm font-medium text-amber-700 hover:text-amber-800"
                  >
                    Manage Subscription →
                  </NavLink>
                </div>
              </div>
            </div>
          )}

          <section>
            <h2 className="text-lg font-semibold mb-4">Select an action</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <ActionCard icon={User} title="Recipients" link='/recipient' />
              <ActionCard icon={Upload} title="Add Media" link='/add-media' />
              <ActionCard icon={Clock} title="Schedule Media" link='/scheduled-media' />
              <ActionCard icon={Image} title="My Media" link='/my-media' />
              {/* <ActionCard icon={Image} title="My Media" link='/my-media' /> */}
              <ActionCard icon={Users} title="Group Media" link='/group-media' />


              <ActionCard icon={Key} title="Legacy" link='/legacy' />
              <ActionCard icon={Plus} title="Upgrade" link='/upgrade' />
            </div>
          </section>

          {/* Commented out favorites section - keeping for reference */}
          {/* <section className="mt-12">
            <h2 className="text-lg font-semibold mb-4">Favorites</h2>
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <div className="text-gray-500 mb-4">You currently don't have any favorites</div>
              <NavLink to={'/add-media'} className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600">
                Add media
              </NavLink>
            </div>
          </section> */}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;